import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTrance } from './TranceContext';
import { Button } from './ui/button';
import { ArrowDown } from 'lucide-react';
import useScrambleText from '../hooks/useScrambleText';

const Hero = () => {
  const { isTranceMode } = useTrance();
  const { scrollY } = useScroll();
  
  // Optimized parallax values
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  // Scramble text effect
  const h2Ref = useScrambleText("Dharmar Records Presents");
  const h1Ref = useScrambleText("KOROV");

  return (
    <section className="relative h-screen w-full overflow-hidden flex items-center justify-center">
      {/* Background Image with Parallax */}
      <motion.div 
        style={{ y: y1 }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background z-10" />
        <img 
          src="https://images.unsplash.com/photo-1632231115662-3eed76916031?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwc3l0cmFuY2UlMjBmb3Jlc3R8ZW58MHx8fHwxNzYzODU1NTAxfDA&ixlib=rb-4.1.0&q=85" 
          alt="Mystical Forest" 
          className="w-full h-full object-cover opacity-60"
        />
      </motion.div>

      {/* Sacred Geometry Overlay (Spinning - SLOWER) */}
      <motion.div 
        className="absolute inset-0 z-0 flex items-center justify-center opacity-20 pointer-events-none will-change-transform"
        animate={{ rotate: 360 }}
        transition={{ duration: isTranceMode ? 40 : 120, repeat: Infinity, ease: "linear" }}
      >
        <img 
          src="https://images.unsplash.com/photo-1619879310659-01e83a8e9d6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxzYWNyZWQlMjBnZW9tZXRyeXxlbnwwfHx8fDE3NjM4NTU1MDZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Mandala"
          className="w-[150vw] max-w-none mix-blend-screen"
        />
      </motion.div>

      {/* Content */}
      <motion.div 
        style={{ y: y2, opacity }}
        className="relative z-20 text-center px-4 will-change-transform"
      >
        <motion.h2 
          ref={h2Ref}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-primary font-heading tracking-[0.5em] text-sm md:text-xl mb-4 uppercase hover:text-glow transition-all duration-300 cursor-pointer"
        >
          Dharmar Records Presents
        </motion.h2>
        
        <motion.h1 
          ref={h1Ref}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className={`text-6xl md:text-9xl font-heading font-black mb-6 tracking-tighter cursor-pointer transition-all duration-500
            ${isTranceMode 
              ? 'animate-pulse text-transparent bg-clip-text bg-gradient-to-r from-primary via-white to-primary' 
              : 'korov-gradient-text hover:scale-105 hover:drop-shadow-[0_0_25px_rgba(var(--primary),0.6)]'
            }`}
        >
          KOROV
        </motion.h1>

        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-muted-foreground text-lg md:text-2xl max-w-2xl mx-auto font-light mb-10"
        >
          Successor of the Old School Sound. <br/>
          <span className="text-primary font-medium">Belgrade, Serbia.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Button 
            size="lg" 
            className="bg-primary text-black hover:bg-primary/80 hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] font-heading tracking-widest text-lg px-8 py-6 rounded-none skew-x-[-10deg] transition-all duration-300"
            onClick={() => document.getElementById('releases').scrollIntoView({ behavior: 'smooth' })}
          >
            LATEST RELEASES
          </Button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 text-foreground/50"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ArrowDown className="w-8 h-8" />
      </motion.div>
    </section>
  );
};

export default Hero;
